import { Family } from "protogen/advisors_service_pb";
import useIsMobile from "../hooks/useIsMobile";
import { useEffect } from "react";
import { useAdminListAllAdvisors, useListAllFamilies } from "services/admin";
import { Box } from "@mui/material";
import CheckedDropDown from "../common/CheckedDropDown";
import { TIME_RANGES } from "../creation/TimeRangeSelect";
import SearchCheckedDropDown from "../common/SearchCheckedDropDown";

// const statusOptions = () => {
//   return [
//     {
//       label: "Any status",
//       value: "",
//       mutuallyExclusive: true,
//     },
//     {
//       label: "Pending Approval",
//       value: "pending_approval",
//       mutuallyExclusive: true,
//     },
//     {
//       label: "Approved",
//       value: "approved",
//       mutuallyExclusive: true,
//     },
//     {
//       label: "Paid",
//       value: "paid",
//       mutuallyExclusive: true,
//     },
//     {
//       label: "Deferred",
//       value: "deferred",
//       mutuallyExclusive: true,
//     },
//   ];
// };

const timeRangeToSecondsAgo = (timeRange: string) => {
  const daysAgo = (days: number) =>
    Math.floor(Date.now() / 1000) - days * 24 * 60 * 60;
  const monthsAgo = (months: number) =>
    Math.floor(Date.now() / 1000) - months * 30 * 24 * 60 * 60;

  // Parse the timeRange string into a tuple
  const parseTimeRange = (range: string): [string, number] | null => {
    const match = range.match(/(\w+)\(([-\d]+)\)/);
    return match ? [match[1], parseInt(match[2], 10)] : null;
  };

  const parsed = parseTimeRange(timeRange);

  if (!parsed) return null;

  const [unit, value] = parsed;

  switch (unit) {
    case "days":
      return daysAgo(-value);
    case "months":
      return monthsAgo(-value);
    default:
      return null;
  }
};

const advisorOptions = (advisors: any[]) => {
  const options = advisors
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((advisor) => ({
      label: advisor.displayName,
      value: advisor.ref,
      mutuallyExclusive: true,
    }));
  options.unshift({
    label: "Any advisor",
    value: "",
    mutuallyExclusive: true,
  });
  return options;
};

const familyOptions = (families: Family[]) => {
  const getLabel = (family: Family) => {
    const members = family.familyMembers.sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
    return `${family.name} (${members.map((m) => m.firstName).join(", ")})`;
  };

  const options = families
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((family: Family) => ({
      label: getLabel(family),
      value: family.ref || "",
      mutuallyExclusive: true,
    }));
  options.unshift({
    label: "Any family",
    value: "",
    mutuallyExclusive: true,
  });
  return options;
};

export type InvoiceFilter = {
  createdAfterSeconds: number | null;
  useLastActionDate: boolean;
  familyRef: string | null;
  advisorRef: string | null;
};

const DATE_FIELD_OPTIONS = [
  { label: "Created", value: "created", mutuallyExclusive: true },
  { label: "Last action", value: "last_action", mutuallyExclusive: true },
];

export default ({
  loading,
  setFilter,
  filter,
}: {
  loading: boolean;
  setFilter: (f: InvoiceFilter) => void;
  filter: InvoiceFilter;
}) => {
  const isMobile = useIsMobile();
  const {
    request: advisorsRequest,
    data: advisorsData,
    loading: advisorLoading,
  } = useAdminListAllAdvisors();
  const {
    request: familiesRequest,
    data: familiesData,
    loading: familyLoading,
  } = useListAllFamilies();
  useEffect(() => {
    advisorsRequest();
    familiesRequest();
  }, []);
  return (
    <Box
      display={"flex"}
      flexDirection="row"
      sx={{ overflowX: isMobile ? "scroll" : "auto" }}
      justifyContent={!isMobile ? "end" : undefined}
    >
      <Box
        display={"flex"}
        flexDirection="row"
        justifyContent="end"
        gap="10px"
        mb={isMobile ? "20px" : undefined}
      >
        <SearchCheckedDropDown
          disabled={loading || advisorLoading}
          placeholder="Advisor"
          options={advisorOptions(advisorsData?.advisors || [])}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          disableClearable={true}
          value={
            filter.advisorRef
              ? advisorOptions(advisorsData?.advisors || []).find(
                  (a) => a.value === filter.advisorRef,
                ) || null
              : null
          }
          onChange={(option) => {
            setFilter({
              ...filter,
              advisorRef: option.value,
            });
          }}
        />
        <SearchCheckedDropDown
          disabled={loading || familyLoading}
          placeholder="Family"
          options={familyOptions(familiesData?.families || [])}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          disableClearable={true}
          value={
            filter.familyRef
              ? familyOptions(familiesData?.families || []).find(
                  (f) => f.value === filter.familyRef,
                ) || null
              : null
          }
          onChange={(option) => {
            setFilter({
              ...filter,
              familyRef: option.value,
            });
          }}
        />
        <CheckedDropDown
          disabled={loading}
          radio={true}
          options={Object.values(TIME_RANGES).map((pt) => ({
            label: pt.label,
            value: pt.value || "",
            mutuallyExclusive: true,
          }))}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          displayValue={
            Object.values(TIME_RANGES).find(
              (t) =>
                t.value &&
                timeRangeToSecondsAgo(t.value) === filter.createdAfterSeconds,
            )?.label || "Date"
          }
          onChange={(r) => {
            setFilter({
              ...filter,
              createdAfterSeconds:
                r.length === 1 && r[0].value
                  ? timeRangeToSecondsAgo(r[0].value)
                  : null,
            });
          }}
        />
        <CheckedDropDown
          disabled={loading}
          radio={true}
          options={DATE_FIELD_OPTIONS}
          initial={[filter.useLastActionDate ? "last_action" : "created"]}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          displayValue={filter.useLastActionDate ? "Last action" : "Created"}
          onChange={(r) => {
            setFilter({
              ...filter,
              useLastActionDate: r.length === 1 && r[0].value === "last_action",
            });
          }}
        />
        {/* <CheckedDropDown
          disabled={loading}
          radio={true}
          options={statusOptions()}
          sx={{ width: isMobile ? "50%" : "fit-content", minWidth: "120px" }}
          maxDropdownHeight={"343px"}
          displayValue={
            statusOptions().find((s) => s.value === filter?.statuses?.[0])
              ?.label || "Any Status"
          }
          onChange={(s) => {
            setFilter({
              ...filter,
              statuses: s.length === 1 && s[0].value ? [s[0].value] : null,
            });
          }}
        /> */}
      </Box>
    </Box>
  );
};
